import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  useColorModeValue,
  Heading,
  Card,
  CardBody,
  Select,
  useToast,
  SimpleGrid,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { ArrowBackIcon, CheckIcon } from '@chakra-ui/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const AddClient: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();
  const { user } = useAuth();
  const phoneInputRef = useRef<HTMLInputElement>(null);

  // Determine if in edit mode
  const isEditMode = Boolean(id);

  // State for country code
  const [selectedCountryCode, setSelectedCountryCode] = useState('+91');
  const [menuWidth, setMenuWidth] = useState<number>(200);
  const countryCodes = ['+1', '+44', '+91', '+61', '+49', '+33', '+81'];

  // If edit mode, fetch client from API
  const [formData, setFormData] = useState<any>({
    company: '',
    joinDate: '',
    address: '',
    pointOfContact: {
      name: '',
      designation: '',
      contactNumber: '',
      email: '',
    },
  });

  React.useEffect(() => {
    if (isEditMode && id) {
      import('../services/api')
  .then(m => m.clientsAPI.getById(id))
  .then((res: any) => {
          const data = res.data || {};
          // Parse phone number into country code and local number
          let phoneVal = data.pointOfContact?.contactNumber || '';
          let parsedNumber = '';
          let parsedCode = selectedCountryCode;
          if (phoneVal && phoneVal.startsWith('+')) {
            const sortedCodes = countryCodes.slice().sort((a, b) => b.length - a.length);
            const match = sortedCodes.find(code => phoneVal.startsWith(code));
            if (match) {
              parsedCode = match;
              parsedNumber = phoneVal.slice(match.length);
            } else {
              parsedCode = selectedCountryCode;
              parsedNumber = phoneVal.replace(/^\+/, '');
            }
          } else {
            parsedNumber = phoneVal;
          }
          setSelectedCountryCode(parsedCode);
          setFormData({
            company: data.company || data.name || data.companyName || '',
            joinDate: data.joinDate || data.join_date || '',
            address: data.address || '',
            pointOfContact: {
              name: data.pointOfContact?.name || '',
              designation: data.pointOfContact?.designation || '',
              contactNumber: parsedNumber || '',
              email: data.pointOfContact?.email || '',
            },
          });
        })
  .catch(() => {
          // Ignore - keep defaults
        });
    }
  }, [isEditMode, id]);

  // Set menu width to match phone input
  React.useEffect(() => {
    if (phoneInputRef.current) {
      setMenuWidth(phoneInputRef.current.offsetWidth);
    }
  }, [phoneInputRef.current]);

  // Update menu width on window resize
  React.useEffect(() => {
    const onResize = () => {
      if (phoneInputRef.current) setMenuWidth(phoneInputRef.current.offsetWidth);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate phone number length (max 10 digits)
      if (formData.pointOfContact.contactNumber && formData.pointOfContact.contactNumber.length > 10) {
        setIsSubmitting(false);
        toast({
          title: 'Invalid phone number',
          description: 'Phone number should not be more than 10 digits.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        return;
      }

      // Compose full phone number with country code
      const composedPhone = `${selectedCountryCode}${formData.pointOfContact.contactNumber}`;

      // Prepare payload
      const payload = {
        ...formData,
        pointOfContact: {
          ...formData.pointOfContact,
          contactNumber: composedPhone,
        },
      };

      if (isEditMode && id) {
        await import('../services/api').then(m => m.clientsAPI.update(id, payload));
        toast({
          title: 'Client Updated Successfully',
          description: `${formData.company}'s details have been updated.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      } else {
        await import('../services/api').then(m => m.clientsAPI.create(payload));
        toast({
          title: 'Client Added Successfully',
          description: `${formData.company} has been added to your client list.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
      }
      navigate('/clients');
    } catch (error: any) {
      toast({
        title: isEditMode ? 'Error Updating Client' : 'Error Adding Client',
        description: error.message || 'There was an error. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/clients');
  };

  return (
    <Box>
      {/* Page Header */}
      <Box mb={8}>
        <HStack spacing={4} mb={4}>
          <Button
            leftIcon={<ArrowBackIcon />}
            variant="ghost"
            onClick={handleCancel}
            color="gray.600"
          >
            Back to Clients
          </Button>
        </HStack>
        <Heading size="lg" color="gray.800" mb={2}>
          {isEditMode ? 'Edit Client' : 'Onboard New Client'}
        </Heading>
        <Text color="gray.600">
          {isEditMode ? 'Update client details and save changes.' : 'Add a new client to your management system'}
        </Text>
      </Box>

      {/* Add/Edit Client Form */}
      <Card bg={cardBg} border="1px" borderColor={borderColor}>
        <CardBody p={8}>
          <form onSubmit={handleSubmit}>
            <VStack spacing={6} align="stretch">
              {/* Basic Information */}
              <Box>
                <Heading size="md" color="gray.700" mb={4}>
                  Company Information
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {/* First row: Company Name (left) and Join Date (right) */}
                  <FormControl isRequired>
                    <FormLabel>Company Name</FormLabel>
                    <Input
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      placeholder="Enter company name"
                      className="form-input"
                    />
                  </FormControl>

                  <FormControl>
                    <FormLabel>Effective Date</FormLabel>
                    <Input
                      type="date"
                      value={formData.joinDate}
                      onChange={(e) => setFormData({ ...formData, joinDate: e.target.value })}
                      className="form-input"
                    />
                  </FormControl>

                  {/* Second row: Address spanning both columns on md+ */}
                  <FormControl gridColumn={{ base: 'auto', md: '1 / -1' }}>
                    <FormLabel>Address</FormLabel>
                    <Textarea
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      placeholder="Enter company address"
                      rows={2}
                      className="form-input"
                    />
                  </FormControl>
                </SimpleGrid>
              </Box>

              {/* Point of Contact Section */}
              <Box>
                <Heading size="md" color="gray.700" mb={4}>
                  Point of Contact
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <FormControl isRequired>
                    <FormLabel>Name</FormLabel>
                    <Input
                      value={formData.pointOfContact.name}
                      onChange={(e) => setFormData({ ...formData, pointOfContact: { ...formData.pointOfContact, name: e.target.value } })}
                      placeholder="Enter contact person's name"
                      className="form-input"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Designation</FormLabel>
                    <Input
                      value={formData.pointOfContact.designation}
                      onChange={(e) => setFormData({ ...formData, pointOfContact: { ...formData.pointOfContact, designation: e.target.value } })}
                      placeholder="Enter contact person's designation"
                      className="form-input"
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Contact Number</FormLabel>
                    <HStack>
                      <Menu>
                        <MenuButton as={Button} size="sm" variant="outline" minW="60px">
                          {selectedCountryCode}
                        </MenuButton>
                        <MenuList minW={`${menuWidth}px`}>
                          <MenuItem onClick={() => setSelectedCountryCode('+1')}>+1 (USA)</MenuItem>
                          <MenuItem onClick={() => setSelectedCountryCode('+44')}>+44 (UK)</MenuItem>
                          <MenuItem onClick={() => setSelectedCountryCode('+91')}>+91 (India)</MenuItem>
                          <MenuItem onClick={() => setSelectedCountryCode('+61')}>+61 (Australia)</MenuItem>
                          <MenuItem onClick={() => setSelectedCountryCode('+49')}>+49 (Germany)</MenuItem>
                          <MenuItem onClick={() => setSelectedCountryCode('+33')}>+33 (France)</MenuItem>
                          <MenuItem onClick={() => setSelectedCountryCode('+81')}>+81 (Japan)</MenuItem>
                        </MenuList>
                      </Menu>
                      <Input
                        ref={phoneInputRef}
                        value={formData.pointOfContact.contactNumber}
                        onChange={(e) => {
                          const digitsOnly = e.target.value.replace(/\D/g, '');
                          const limited = digitsOnly.slice(0, 10);
                          setFormData({ ...formData, pointOfContact: { ...formData.pointOfContact, contactNumber: limited } });
                        }}
                        placeholder="Enter contact person's phone number"
                        className="form-input"
                      />
                    </HStack>
                  </FormControl>
                  <FormControl isRequired>
                    <FormLabel>Email</FormLabel>
                    <Input
                      type="email"
                      value={formData.pointOfContact.email}
                      onChange={(e) => setFormData({ ...formData, pointOfContact: { ...formData.pointOfContact, email: e.target.value } })}
                      placeholder="Enter contact person's email"
                      className="form-input"
                    />
                  </FormControl>
                </SimpleGrid>
              </Box>

              {/* Form Actions */}
              <HStack spacing={4} justify="flex-end" pt={4}>
                <Button
                  variant="ghost"
                  onClick={handleCancel}
                  size="lg"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  colorScheme="primary"
                  leftIcon={<CheckIcon />}
                  isLoading={isSubmitting}
                  loadingText={isEditMode ? 'Updating Client...' : 'Adding Client...'}
                  size="lg"
                >
                  {isEditMode ? 'Save Changes' : 'Onboard Client'}
                </Button>
              </HStack>
            </VStack>
          </form>
        </CardBody>
      </Card>
    </Box>
  );
};

export default AddClient;
